import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import { DEFAULT_SECTIONS, DESTINATION_INFO } from './publishOptions';
import {
  ApiVersionChoice,
  DestinationChoice,
  LanguagePicker,
  Notice,
  PublishingSpinner,
  SectionPicker,
} from './PublishModalParts';

const publishButtonLabel = (isUpdate, count) => {
  const action = isUpdate ? 'Update Publish' : 'Publish';
  return count > 1 ? `${action} (${count} languages)` : action;
};

/**
 * Confirmation dialog for publishing a report.
 * onConfirm(languageCodes, digwatchApiVersion?, sections?) — the last two only
 * when publishing to WordPress (sections only when showSections is set).
 */
const PublishModal = ({
  isOpen,
  onClose,
  onConfirm,
  destination,
  availableDestinations = [],
  onDestinationChange,
  isUpdate = false,
  isPublishing = false,
  availableLanguages = [],
  showSections = false,
}) => {
  const completedLangs = availableLanguages.filter((l) => l.status === 'COMPLETED');
  const [selectedLangs, setSelectedLangs] = useState(['en']);
  const [digwatchApiVersion, setDigwatchApiVersion] = useState('production');
  const [sections, setSections] = useState(DEFAULT_SECTIONS);

  // Reset only when the modal opens — not when availableLanguages changes
  // (session page polling refreshes languages every few seconds).
  useEffect(() => {
    if (!isOpen) return;
    const codes = completedLangs.map((l) => l.code);
    setSelectedLangs(codes.length > 0 ? codes : ['en']);
    setDigwatchApiVersion('production');
    setSections(DEFAULT_SECTIONS);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (key) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleLanguage = (code) => {
    setSelectedLangs((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const info = DESTINATION_INFO[destination] || DESTINATION_INFO.wordpress;
  const canSwitch = availableDestinations.length > 1 && onDestinationChange;
  const showDigwatchApiVersion = destination === 'wordpress';
  const showSectionSelector = showSections && destination === 'wordpress';
  const pickLanguages = completedLangs.length > 1;

  const confirm = () => {
    onConfirm(
      pickLanguages ? selectedLangs : [completedLangs[0]?.code || 'en'],
      showDigwatchApiVersion ? digwatchApiVersion : undefined,
      showSectionSelector ? sections : undefined,
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isUpdate ? 'Update Published Report' : 'Publish Report'}>
      <div className="space-y-4">
        {canSwitch && (
          <DestinationChoice
            destinations={availableDestinations}
            destination={destination}
            onChange={onDestinationChange}
            disabled={isPublishing}
          />
        )}

        {showDigwatchApiVersion && (
          <ApiVersionChoice version={digwatchApiVersion} onChange={setDigwatchApiVersion} disabled={isPublishing} />
        )}

        {showSectionSelector && (
          <SectionPicker sections={sections} onToggle={toggleSection} destinationName={info.name} disabled={isPublishing} />
        )}

        <Notice tone="info" Icon={CheckCircleIcon} title={`Publishing to ${info.name}`}>
          {info.description}
        </Notice>

        {isUpdate && (
          <Notice tone="warning" Icon={ExclamationTriangleIcon} title="Warning: This will overwrite existing publish">
            The previously published report will be replaced with the new version. This action cannot be undone.
          </Notice>
        )}

        {pickLanguages && (
          <LanguagePicker
            languages={completedLangs}
            selected={selectedLangs}
            onToggle={toggleLanguage}
            onSelectAll={() => setSelectedLangs(completedLangs.map((l) => l.code))}
            onClear={() => setSelectedLangs([])}
            destinationName={info.name}
            disabled={isPublishing}
          />
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isPublishing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={isPublishing || (pickLanguages && selectedLangs.length === 0)}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              isUpdate
                ? 'bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            {isPublishing
              ? <PublishingSpinner />
              : publishButtonLabel(isUpdate, pickLanguages ? selectedLangs.length : 1)}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PublishModal;